import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import * as chrono from 'chrono-node';
import Select, { components as SelectComponents } from 'react-select';
import {COLORS as COLOR, COLORS} from '../../theme';
import { Button, Col, Row } from 'react-bootstrap';
import { Icon } from '../../components'
import styled from "styled-components";

const createOptionForDate = d => {
  const date = moment.isMoment(d) ? d : moment(d);
  return {
    date,
    value: date.toDate(),
    label: date.calendar(null, {
      sameDay: '[Today] (Do MMM YYYY)',
      nextDay: '[Tomorrow] (Do MMM YYYY)',
      nextWeek: '[Next] dddd (Do MMM YYYY)',
      lastDay: '[Yesterday] (Do MMM YYYY)',
      lastWeek: '[Last] dddd (Do MMM YYYY)',
      sameElse: 'Do MMMM YYYY',
    }),
  };
};

const defaultSelectOptions = ['today', 'tomorrow', 'yesterday'].map(i =>
  createOptionForDate(chrono.parseDate(i))
);

const createCalendarOptions = (date = null) => {
  if (!date) {
    date = new Date();
  }
  const daysInMonth = Array.apply(null, {
    length: moment(date).daysInMonth(),
  }).map((x, i) => {
    const d = moment(date).date(i + 1);
    return { ...createOptionForDate(d), display: 'calendar' };
  });
  return {
    label: moment(date).format('MMMM YYYY'),
    options: daysInMonth,
  };
};

const suggestions = [
  'sunday',
  'saturday',
  'friday',
  'thursday',
  'wednesday',
  'tuesday',
  'monday',
  'december',
  'november',
  'october',
  'september',
  'august',
  'july',
  'june',
  'may',
  'april',
  'march',
  'february',
  'january',
  'yesterday',
  'tomorrow',
  'today',
].reduce((acc, str) => {
  for (let i = 1; i < str.length; i++) {
    acc[str.substr(0, i)] = str;
  }
  return acc;
}, {});

const suggest = str =>
  str
    .split(/\b/)
    .map(i => suggestions[i] || i)
    .join('');

const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const daysHeaderStyles = {
  maxWidth: '400px',
  margin: 'auto'
};

const daysHeaderItemStyles = {
  color: '#999',
  cursor: 'default',
  // display: 'block',
  fontSize: '75%',
  fontWeight: '500',
  display: 'inline-block',
  width: '12%',
  margin: '0 1%',
  textAlign: 'center',
};

const DaysContainer = styled.div`
  max-width: 400px;
  margin: auto;
`

const HoveredSpan = styled.span`
  &:hover {
    background-color: ${COLORS.primary}50 !important;
  }
`

const getOptionStyles = defaultStyles => ({
  ...defaultStyles,
  display: 'inline-block',
  width: '12%',
  margin: '0 1%',
  textAlign: 'center',
  borderRadius: '4px'
});

const Option = props => {
  const { data, getStyles, innerRef, innerProps } = props;
  if (data.display === 'calendar') {
    const defaultStyles = getStyles('option', props);
    const styles = getOptionStyles(defaultStyles);
    if (data.date.date() === 1) {
      const indentBy = (data.date.day() + 6)%7;
      if (indentBy) {
        styles.marginLeft = `${indentBy * 14 + 1}%`;
      }
    }
    return (
      <HoveredSpan {...innerProps} style={styles} ref={innerRef}>
        {data.date.format('D')}
      </HoveredSpan>
    );
  } else return <SelectComponents.Option {...props} />;
};

const buttonsStyle = {
  padding: '2px 7px'
}

const DropdownIndicator = props => {
  return (
    <SelectComponents.DropdownIndicator {...props}>
      <Icon icon='calendar' />
    </SelectComponents.DropdownIndicator>
  );
};

function DatePicker(props) {
  const [options, setOptions] = useState([]);
  const [selectDates, setSelectDates] = useState([null, null]);

  const { isInvalid, isValid, value } = props

  const Group = props => {
    const {
      Heading,
      getStyles,
      children,
      label,
      innerProps,
      headingProps,
      cx,
      theme
    } = props;
    return (
      <div aria-label={label} style={getStyles('group', props)} {...innerProps}>
        <Heading theme={theme} getStyles={getStyles} cx={cx} {...headingProps} style={{borderBottom: "1px solid #eee", paddingBottom: "5px"}}>
          <Row>
            <Col><Button style={buttonsStyle} onClick={prevMonth}>
              <Icon icon='caret-left' size='lg' style={{marginRight: '2px'}} />
            </Button></Col>
            <Col style={{textAlign: 'center', paddingTop: '3px', fontSize: '1.25em', color: COLORS.primary}} className="my-auto">
              {label}
            </Col>
            <Col><Button style={{...buttonsStyle, float: 'right'}} onClick={nextMonth}>
              <Icon icon='caret-right' size='lg' style={{marginLeft: '1.5px', marginRight: '1.5px'}}/>
            </Button></Col>
          </Row>
        </Heading>
        <div style={daysHeaderStyles}>
          {days.map((day, i) => (
            <span key={`${i}-${day}`} style={daysHeaderItemStyles}>
              {day}
            </span>
          ))}
        </div>
        <DaysContainer>{children}</DaysContainer>
      </div>
    );
  };

  const customStyles = {
    control: (provided, state) => {
      const isInvalidStyle = isInvalid ? {borderColor: "#dc3545"} : {};
      const isValidStyle = isValid ? {borderColor: "#28a745"} : {};
      const focusedStyle = state.isFocused ? {
        color: COLORS.darkGrey,
        borderColor: COLORS.primary,
        outline: 0,
        boxShadow: `0 0 0 0.2rem ${COLORS.primary}50`
      } : {}
      return ({
        ...provided,
        ...focusedStyle,
        ...isInvalidStyle,
        ...isValidStyle,
        "&:hover": {
          borderColor: null
        },
        backgroundColor: state.isDisabled ? "#e9ecef" : "initial"
      })
    },
    singleValue: (provided, state) => ({
      color: state.isDisabled ? "#495057" : "initial"
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? COLORS.primary : 'initial',
      color: state.isSelected ? 'white' : 'initial',
      "&:hover": {
        backgroundColor: `${COLORS.primary}50`
      }
    }),
    valueContainer: (provided, state) => {
      const isInvalidStyle = isInvalid ? {
        paddingRight: "calc(1.5em + 0.75rem)",
        backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='%23dc3545' viewBox='-2 -2 7 7'%3e%3cpath stroke='%23dc3545' d='M0 0l3 3m0-3L0 3'/%3e%3ccircle r='.5'/%3e%3ccircle cx='3' r='.5'/%3e%3ccircle cy='3' r='.5'/%3e%3ccircle cx='3' cy='3' r='.5'/%3e%3c/svg%3E\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center right calc(0.375em + 0.1875rem)",
        backgroundSize: "calc(0.75em + 0.375rem) calc(0.75em + 0.375rem)"
      } : {};
      const isValidStyle = isValid ? {
        paddingRight: "calc(1.5em + 0.75rem)",
        backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3e%3cpath fill='%2328a745' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center right calc(0.375em + 0.1875rem)",
        backgroundSize: "calc(0.75em + 0.375rem) calc(0.75em + 0.375rem)"
      } : {};
      return {
        ...provided,
        ...isValidStyle,
        ...isInvalidStyle
      }
    }
  }

  function handleInputChange(value) {
    if (!value) {
      setOptions(defaultOptions);
      return;
    }
    const date = chrono.parseDate(suggest(value.toLowerCase()));
    if (date) {
      setOptions(buildOptions(date, date));
    } else {
      setOptions([]);
    }
  }

  function nextMonth() {
    changeMonth(1);
  }

  function prevMonth() {
    changeMonth(-1);
  }

  function changeMonth(x) {
    let [currentDate, currentCalendarDate] = selectDates;
    if (!currentCalendarDate) {
      currentCalendarDate = new Date();
    }
    const nextCalendarDate = moment(currentCalendarDate).add(x, 'M');
    setSelectDates([currentDate, nextCalendarDate]);
  }

  useEffect(() => {
    const o = buildOptions(...selectDates);
    setOptions(o);
  }, [selectDates]);

  const buildOptions = (date, calendarDate) => {
    return [...(date ? createOptionForDate(date) : defaultSelectOptions), createCalendarOptions(calendarDate)]
  }

  const defaultOptions = () => ([...defaultSelectOptions, createCalendarOptions()]);

  const handleChange = (opt, action) => {
    const val = opt ? opt.date.format('YYYY-MM-DD') : opt
    props.onChange(val)
  }

  return (
    <Select
      {...props}
      components={{ Group, Option, DropdownIndicator }}
      filterOption={null}
      isMulti={false}
      isOptionSelected={(o, v) => v.some(i => i.date.isSame(o.date, 'day'))}
      maxMenuHeight={380}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={options}
      isClearable={props.isClearable}
      value={value ? createOptionForDate(value) : value}
      menuPortalTarget={document.querySelector('body')}
      styles={customStyles}
    />
  )
}


DatePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  isValid: PropTypes.bool,
  isInvalid: PropTypes.bool,
  // value: PropTypes.string
}

export default DatePicker;
